import { RoundedLogo } from '@assets/newlogo';
import { ButtonLabel } from '@moreharvest/component-library';
import DesktopAuthWrapper from '@components/atoms/DesktopAuthWrapper';
import { useProfile, useVerify } from '@hooks/api';
import { login, reauth } from '@services/analytics';
import { useLoadingModal } from '@hooks/useLoadingModal';
import { useReauthParamsURL } from '@hooks/useReauthParams';
import { useRedirect } from '@hooks/useRedirect';
import {
    AnalyticsAuthErrorCode,
    AnalyticsAuthMethod,
    AnalyticsAuthStage,
    SecretType,
    VerifyAuthRequest,
} from 'mh-client';
import { startAuthentication } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/typescript-types';
import { useAppSelector } from '@store/index';
import { selectProfileAuthInfo } from '@store/reducers/profile';
import { FormattedMessage } from '@utils/i18n';
import { sendToSentry } from '@utils/sentry';
import { stripNulls } from '@utils/webauthn';
import { useNavigate } from 'react-router';

const LoginWithWebAuthn = () => {
    const navigate = useNavigate();
    const { mutateAsync: verify, isPending } = useVerify();
    const profileAuthInfo = useAppSelector(selectProfileAuthInfo);

    const { url, anotherMethodUrl, isReauth, scopes } = useReauthParamsURL();
    const fireAuth = isReauth ? reauth : login;

    useRedirect(profileAuthInfo.email.length > 0, '/login');

    const { data: userProfile, isLoading: profileIsLoading } = useProfile({
        enabled: !profileAuthInfo.email,
    });

    useLoadingModal(isPending || profileIsLoading);

    const getChallenge = async () => {
        const email = userProfile?.email ?? profileAuthInfo.email;
        fireAuth({
            method: AnalyticsAuthMethod.Passkey,
            stage: AnalyticsAuthStage.Initiated,
        });
        try {
            const firstStepData = await verify(
                new VerifyAuthRequest(
                    email,
                    SecretType.webauthn,
                    '', // empty secret for first step
                    scopes
                )
            );
            if (!firstStepData.requestChallengeResponse?.publicKey)
                throw new Error('No challenge response');

            const publicKey = firstStepData.requestChallengeResponse.publicKey;
            console.log(
                'WebAuthn auth options:',
                JSON.stringify(publicKey, null, 2)
            );
            console.log(
                'allowCredentials type:',
                Array.isArray(publicKey.allowCredentials)
            );
            console.log(
                'transports type:',
                Array.isArray(publicKey.allowCredentials?.[0]?.transports)
            );
            console.log(
                'transports value:',
                publicKey.allowCredentials?.[0]?.transports
            );

            const publicKeyOptions = stripNulls(
                publicKey
            ) as PublicKeyCredentialRequestOptionsJSON;
            const attResp = await startAuthentication(publicKeyOptions);

            await verify(
                new VerifyAuthRequest(
                    email,
                    SecretType.webauthn,
                    JSON.stringify(attResp),
                    scopes
                )
            );
            fireAuth({
                method: AnalyticsAuthMethod.Passkey,
                stage: AnalyticsAuthStage.Success,
            });
            navigate(url);
        } catch (error) {
            fireAuth({
                method: AnalyticsAuthMethod.Passkey,
                stage: AnalyticsAuthStage.Failed,
                error_code: AnalyticsAuthErrorCode.Unknown,
            });
            sendToSentry(
                'LoginWithWebAuthn',
                'error logging in with webauthn',
                {
                    originalError: error,
                }
            );
        }
    };

    const handleAnotherMethod = async () => {
        if (profileAuthInfo.methods.includes('password') && !isReauth) {
            fireAuth({
                method: AnalyticsAuthMethod.Password,
                stage: AnalyticsAuthStage.MethodSwitch,
            });
            navigate('/login/password' + anotherMethodUrl);
        } else {
            fireAuth({
                method: AnalyticsAuthMethod.MagicLink,
                stage: AnalyticsAuthStage.MethodSwitch,
            });
            navigate('/login/send-magic-link' + anotherMethodUrl, {
                state: {
                    force: true,
                },
            });
        }
    };

    return (
        <DesktopAuthWrapper>
            <div
                className="flex
                flex-col
                flex-1
                w-full
                justify-center
                items-center my-auto gap-y-12"
            >
                <RoundedLogo size="small" />
                <p className="Body-Medium text-center">
                    <FormattedMessage id="webauthn_body" />
                </p>
                <div className="flex flex-col gap-y-4 items-center w-full">
                    <ButtonLabel
                        variant="solid"
                        size="medium"
                        type="button"
                        onClick={() => {
                            getChallenge();
                        }}
                        id="loginPasskey_CTA_continue"
                    >
                        <FormattedMessage id="continue_upper" />
                    </ButtonLabel>
                    <ButtonLabel
                        variant="empty"
                        size="small"
                        type="button"
                        onClick={handleAnotherMethod}
                    >
                        <FormattedMessage id="use_another_method" />
                    </ButtonLabel>
                </div>
            </div>
        </DesktopAuthWrapper>
    );
};

export default LoginWithWebAuthn;
